import { Box, Button, Flex, Heading, HStack, Icon, IconButton, Link as ChakraLink, Text, useClipboard } from "@chakra-ui/react";
import { motion } from "framer-motion";
import { FaEnvelope, FaGithub, FaGlobe, FaLinkedin } from "react-icons/fa";
import { FiArrowUpRight, FiCheck, FiCopy } from "react-icons/fi";
import { portfolio, type SocialLink } from "../data/portfolio";
import { sectionIndex } from "../data/sections";
import { MotionFade } from "./MotionFade";
import { Accent, EASE_OUT, Eyebrow } from "./SectionHeading";

const MotionBox = motion(Box);

const socialIcon = (icon: SocialLink["icon"]) => {
  switch (icon) {
    case "linkedin":
      return FaLinkedin;
    case "github":
      return FaGithub;
    case "website":
      return FaGlobe;
    default:
      return FaEnvelope;
  }
};

export function ContactSection() {
  const { person, social } = portfolio;
  const { onCopy, hasCopied } = useClipboard(person.email);

  return (
    <Box id="contato" as="section" scrollMarginTop="72px" position="relative" overflow="hidden" bg="#0c0c10">
      <Box
        position="absolute"
        w="620px"
        h="620px"
        borderRadius="full"
        bg="accent.600"
        opacity={0.16}
        filter="blur(120px)"
        bottom="-320px"
        left="50%"
        transform="translateX(-50%)"
        aria-hidden
      />

      <Box position="relative" pt={{ base: 20, md: 32 }} pb={10} px={{ base: 5, md: 8 }}>
        <Box maxW="container.xl" mx="auto">
          <MotionFade mb={6}>
            <Eyebrow index={sectionIndex("contato")} label="Contato" />
          </MotionFade>

          <MotionBox overflow="hidden" pb="0.14em" initial="hidden" whileInView="show" viewport={{ once: true, margin: "-72px" }}>
            <MotionBox
              variants={{
                hidden: { y: "108%" },
                show: { y: 0, transition: { duration: 0.9, ease: EASE_OUT } },
              }}
            >
              <Heading
                as="h2"
                fontSize={{ base: "3.25rem", sm: "4.5rem", md: "6rem", lg: "7.5rem" }}
                lineHeight="0.98"
                letterSpacing="-0.045em"
                color="white"
              >
                Vamos <Accent>conversar?</Accent>
              </Heading>
            </MotionBox>
          </MotionBox>

          <MotionFade delay={0.1}>
            <Text mt={7} fontSize={{ base: "md", md: "lg" }} lineHeight="tall" color="graphite.300" maxW="2xl">
              Se você busca alguém para evoluir apps mobile, produtos web ou integrações críticas, em qualquer segmento, envie o contexto da vaga ou do projeto. Respondo por e-mail, telefone ou pelas redes abaixo.
            </Text>
          </MotionFade>

          <MotionFade delay={0.16}>
            <Flex mt={10} gap={3} flexWrap="wrap" align="center">
              <Button
                as="a"
                href={`mailto:${person.email}`}
                size="lg"
                h={{ base: "56px", md: "64px" }}
                px={{ base: 6, md: 8 }}
                fontSize={{ base: "md", md: "lg" }}
                borderRadius="full"
                colorScheme="accent"
                rightIcon={<FiArrowUpRight />}
                maxW="full"
                transition="transform 0.25s ease, box-shadow 0.25s ease"
                _hover={{ transform: "translateY(-2px)", boxShadow: "0 18px 40px rgba(224, 134, 44, 0.4)" }}
              >
                {person.email}
              </Button>
              <Button
                onClick={onCopy}
                size="lg"
                h={{ base: "56px", md: "64px" }}
                variant="outline"
                borderRadius="full"
                borderColor="whiteAlpha.400"
                color="graphite.100"
                leftIcon={<Icon as={hasCopied ? FiCheck : FiCopy} />}
                _hover={{ bg: "whiteAlpha.100" }}
              >
                <Box as="span" aria-live="polite">
                  {hasCopied ? "Copiado" : "Copiar e-mail"}
                </Box>
              </Button>
              <Button
                as="a"
                href={`tel:${person.phoneTel}`}
                size="lg"
                h={{ base: "56px", md: "64px" }}
                variant="ghost"
                borderRadius="full"
                color="graphite.100"
                _hover={{ bg: "whiteAlpha.100", color: "accent.300" }}
              >
                {person.phoneDisplay}
              </Button>
            </Flex>
          </MotionFade>

          <Flex
            mt={{ base: 16, md: 24 }}
            pt={6}
            borderTopWidth="1px"
            borderColor="whiteAlpha.200"
            direction={{ base: "column", md: "row" }}
            gap={4}
            align={{ md: "center" }}
            justify="space-between"
          >
            <Text fontSize="sm" color="graphite.400">
              © {new Date().getFullYear()} {person.fullName} · {person.location}
            </Text>
            <HStack spacing={1}>
              {social.map((s) => {
                const SocialIcon = socialIcon(s.icon);
                return (
                  <ChakraLink key={s.href} href={s.href} isExternal={s.icon !== "email"}>
                    <IconButton
                      as="span"
                      aria-label={s.label}
                      icon={<SocialIcon />}
                      variant="ghost"
                      borderRadius="full"
                      color="graphite.200"
                      _hover={{ bg: "whiteAlpha.100", color: "accent.300" }}
                    />
                  </ChakraLink>
                );
              })}
            </HStack>
          </Flex>
        </Box>
      </Box>
    </Box>
  );
}
